import React, { useCallback, useMemo } from 'react';

// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useFragment } from 'react-relay';
import { Box, Text } from '@atlaskit/primitives';

import Link from '@atlaskit/link';
import { useIntl, FormattedMessage } from '@atlassian/jira-intl';

import { fg } from '@atlassian/jira-feature-gating';
import {
	FireUiAnalytics,
	fireUIAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { trackOrLogClientError } from '@atlassian/jira-issue-view-errors/src/common/utils/track-error.tsx';
import { fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge/src';
import authenticateUsingAppLinks from '@atlassian/jira-common-authenticate-using-app-links/src/index.tsx';

import { useIsFixedTableLayoutEnabled } from '../../../controllers/features/selectors.tsx';
import { useRowContext } from '../../rows/row-context/index.tsx';
import { TableCell, type Props as TableCellProps } from '../../../common/ui/table-cell/index.tsx';
import { COLUMN_ID_MEATBALL_MENU } from '../../../common/constants.tsx';
import type { remoteIssueErrorRow_nativeIssueTable$key } from './__generated__/remoteIssueErrorRow_nativeIssueTable.graphql';
import messages from './messages.tsx';

const APPLINK_MISSING = 'APPLINK_MISSING' as const;
const APPLINK_REQ_AUTH = 'APPLINK_REQ_AUTH' as const;

export type PropsErrorIssueRow = {
	isSelected: boolean;
	cellBorder?: TableCellProps['border'];
	errorRef: remoteIssueErrorRow_nativeIssueTable$key;
	issueKey?: string | null;
	onAuthenticationClick?: () => void;
};

const RemoteIssueErrorRow = ({
	isSelected,
	cellBorder,
	errorRef,
	issueKey,
	onAuthenticationClick,
}: PropsErrorIssueRow) => {
	// Define fragment inside the component to satisfy Relay linting rules
	const error = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment remoteIssueErrorRow_nativeIssueTable on JiraRemoteLinkedIssueError {
				errorType
				repairLink {
					href
				}
			}
		`,
		errorRef,
	);
	const { formatMessage } = useIntl();
	const { columns, tableInjectedProps } = useRowContext();
	const isFixedTableLayoutEnabled = useIsFixedTableLayoutEnabled();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const hasSpacerColumn = useMemo(
		() => isFixedTableLayoutEnabled && columns.some((c) => c.id === COLUMN_ID_MEATBALL_MENU),
		[isFixedTableLayoutEnabled, columns],
	);

	// Extract remoteServerHostname from repairLink.href
	const remoteServerHostname = useMemo(() => {
		if (error?.repairLink?.href) {
			try {
				return new URL(error.repairLink.href).hostname;
			} catch {
				return null;
			}
		}
		return null;
	}, [error?.repairLink?.href]);

	const getAttributes = useCallback(
		() => ({
			errorType: error?.errorType || 'NONE',
			issueKey: issueKey || 'UNKNOWN',
			remoteServerHostname: remoteServerHostname || 'UNKNOWN',
			isSelected,
			hasRepairLink: !!error?.repairLink?.href,
			hasError: !!error,
			isRemote: !!remoteServerHostname,
			component: 'RemoteIssueErrorRow',
			context: 'issue-table',
		}),
		[error, issueKey, remoteServerHostname, isSelected],
	);

	const handleUserClickAnalytics = useCallback(
		(actionSubjectId: string) => {
			const analyticsEvent = createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'issueView.ErrorIssueRow',
			});
			const attributes = getAttributes();
			fireUIAnalytics(analyticsEvent, actionSubjectId, attributes);
		},
		[createAnalyticsEvent, getAttributes],
	);
	const handleAuthentication = useCallback(async () => {
		// Track authentication click analytics
		handleUserClickAnalytics('errorIssueRow.authenticationLink');
		const analyticsEvent = createAnalyticsEvent({
			action: 'authenticate',
			actionSubject: 'errorIssueRow.authenticationLink',
		});
		// Check if error has repairLink
		if (error?.repairLink?.href) {
			try {
				fireTrackAnalytics(analyticsEvent, 'applink authenticate', {
					authStatus: 'start',
				});
				await authenticateUsingAppLinks(error.repairLink.href);
				fireTrackAnalytics(analyticsEvent, 'applink authenticate', {
					authStatus: 'success',
				});
				// On successful authentication, refetch the table data
				if (tableInjectedProps.onIssueRefetch) {
					tableInjectedProps.onIssueRefetch(issueKey || '');
				}
			} catch (authError) {
				// Track authentication failure for Splunk monitoring
				if (authError instanceof Error || authError instanceof TypeError) {
					trackOrLogClientError(
						'remote-issue-error-row',
						'Failed to authenticate with applinks',
						authError,
					);
				}
			}
		} else if (onAuthenticationClick) {
			// Fallback to the original authentication click handler
			onAuthenticationClick();
		}
	}, [
		error,
		issueKey,
		tableInjectedProps,
		onAuthenticationClick,
		handleUserClickAnalytics,
		createAnalyticsEvent,
	]);

	const renderContent = useMemo(() => {
		switch (error?.errorType) {
			case APPLINK_REQ_AUTH:
				return (
					<Text color={isSelected ? 'color.text.selected' : 'color.text'}>
						<FormattedMessage
							{...messages.linkedWorkItemAuthRequiredWithCta}
							values={{
								cta: (
									<Link
										href={error.repairLink?.href || '#'}
										onClick={(e) => {
											e.preventDefault();
											handleAuthentication();
										}}
									>
										{formatMessage(messages.linkedWorkItemAuthRequiredCta)}
									</Link>
								),
							}}
						/>
					</Text>
				);

			case APPLINK_MISSING:
				return (
					<Text color="color.text.danger">
						<FormattedMessage
							{...messages.linkedWorkItemMissingApplinkWithCta}
							values={{
								cta: (
									<Link
										href="/plugins/servlet/applinks/listApplicationLinks"
										target="_blank"
										onClick={() => handleUserClickAnalytics('errorIssueRow.projectSettingsLink')}
									>
										{formatMessage(messages.linkedWorkItemMissingApplinkCta)}
									</Link>
								),
							}}
						/>
					</Text>
				);
			default:
				return (
					<Text color={isSelected ? 'color.text.selected' : 'color.text'}>
						{formatMessage(
							fg('jira-issue-terminology-refresh-m3')
								? messages.remoteLinkGeneralErrorIssueTermRefresh
								: messages.remoteLinkGeneralError,
						)}
					</Text>
				);
		}
	}, [error, formatMessage, handleAuthentication, isSelected, handleUserClickAnalytics]);

	// Extra column added to account for the blank spacer column added to the table
	return (
		<TableCell colSpan={columns.length + (hasSpacerColumn ? 1 : 0)} border={cellBorder}>
			<Box paddingInlineStart="space.050">{renderContent}</Box>
			<FireUiAnalytics action="viewed" actionSubject="errorRemoteIssue" />
		</TableCell>
	);
};

export default RemoteIssueErrorRow;
